import React, { useEffect } from 'react';
import { ExtrudeGeometry, Shape, Vector3 } from 'three';

import { DistributedLoad, Point2D as Point2DInterface } from '@paragon/contract-library';
import { Direction2D, Point2D } from '@paragon/geometry-library';
import { centerPoint2D } from '@paragon/geometry-library/source/scripts/Polygon2D';
import { newVector3, RenderOrder, Segment3D } from '@paragon/three-shared';

import { getLoadMaterial } from '../../library/getLoadMaterial';
import { VectorVisual, VectorVisualType } from '../util/VectorVisual';

const localXPadding = 0.5;
const arrowBufferAmount = 4;
const inchesPerArrow = 6;

interface Props {
  readonly load: Omit<DistributedLoad, 'loadCaseGuid'>;
  readonly offset: Point2DInterface;
  readonly startArrowMagnitude: number;
  readonly endArrowMagnitude: number;
  readonly labels: React.JSX.Element;
}

export function DistributedLoadVisual({ load, offset, startArrowMagnitude, endArrowMagnitude, labels }: Props) {
  const direction = newVector3({ ...load.direction, z: 0 }).normalize();
  const arrowBuffer = new Vector3().copy(direction).negate().multiplyScalar(arrowBufferAmount);
  const start = new Vector3(load.start.x, load.start.y, 0);
  const end = new Vector3(load.end.x, load.end.y, 0);
  const vector = new Vector3().copy(end).sub(start);
  const localStart = new Vector3().add(arrowBuffer);
  const localEnd = new Vector3().copy(vector).add(arrowBuffer);
  const startPointBeginning = new Vector3().copy(direction).multiplyScalar(-startArrowMagnitude).add(localStart);
  const endPointBeginning = new Vector3().copy(direction).multiplyScalar(-endArrowMagnitude).add(localEnd);
  const depth = 1;
  const halfDepth = depth / 2;
  const loadShape = new Shape();
  loadShape.moveTo(localStart.x + localXPadding, localStart.y);
  loadShape.lineTo(localEnd.x - localXPadding, localEnd.y);
  loadShape.lineTo(endPointBeginning.x - localXPadding, endPointBeginning.y);
  loadShape.lineTo(startPointBeginning.x + localXPadding, startPointBeginning.y);
  const loadGeometry = new ExtrudeGeometry(loadShape, { depth, bevelEnabled: false });
  loadGeometry.translate(0, 0, -halfDepth);

  useEffect(() => () => void loadGeometry.dispose());

  const directionArrowCount = startPointBeginning.distanceTo(endPointBeginning) / inchesPerArrow;
  const arrows: Segment3D[] = [];
  for (let i = 0; i < directionArrowCount; i += 1) {
    let arrowStart = new Vector3().lerpVectors(startPointBeginning, endPointBeginning, i / directionArrowCount);
    let arrowEnd = new Vector3().lerpVectors(localStart, localEnd, i / directionArrowCount);
    if (i === 0) {
      arrowStart.x += localXPadding;
      arrowEnd.x += localXPadding;
    }
    if (Math.abs(i - directionArrowCount) < 1) {
      arrowStart = new Vector3().copy(endPointBeginning);
      arrowEnd = new Vector3().copy(localEnd);
      arrowStart.x -= localXPadding;
      arrowEnd.x -= localXPadding;
    }
    arrows.push({ start: arrowStart, end: arrowEnd });
  }

  return (
    <>
      <object3D position={[load.start.x + offset.x, load.start.y + offset.y, 0]}>
        <mesh
          renderOrder={RenderOrder.DistributedLoadVisual}
          geometry={loadGeometry}
          material={getLoadMaterial(load.loadType)}
        />
        {arrows.map((arrowSegment) => (
          <VectorVisual
            key={arrowSegment.start.x}
            thickness={0.2}
            segment={arrowSegment}
            vectorVisualType={VectorVisualType.CadmiumYellow}
          />
        ))}
      </object3D>
      {labels}
    </>
  );
}

export function getInitialUniformLoadLabelPosition(
  loadStartLocation: Point2D,
  loadEndLocation: Point2D,
  loadDirection: Direction2D,
  arrowMagnitude: number,
) {
  const loadCenterPoint = centerPoint2D([loadStartLocation, loadEndLocation]);
  const offsetToMiddleOfLoadArrows = arrowBufferAmount + arrowMagnitude / 2;
  return loadCenterPoint.translate(loadDirection.toVector(-offsetToMiddleOfLoadArrows));
}

export function getInitialTrapezoidalLoadLabelPosition(
  loadLocation: Point2D,
  loadDirection: Direction2D,
  arrowMagnitude: number,
  loadSegmentDirection: Direction2D,
  paddingOffsetSign: number,
) {
  const arrowBaseOffset = arrowBufferAmount + arrowMagnitude;
  const additionalLabelOffset = arrowBufferAmount / 2;
  const labelOffset = arrowBaseOffset + additionalLabelOffset;

  return loadLocation
    .translate(loadDirection.toVector(-labelOffset))
    .translate(loadSegmentDirection.toVector(paddingOffsetSign * localXPadding * 5));
}
